import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  X,
  Search,
  Loader,
  Tag,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { formatNumberWithSeparator } from "../utils/numberFormat";
import * as inventoryService from "../api/inventoryService";
import { categoryService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";

const PAGE_SIZE = 100;

// Shared numeric input used in the stock config table.
// - inputMode="numeric" pattern="[0-9]*" ensures mobile numeric keyboard on both
//   browser and Capacitor (Android/iOS) APK.
// - We store raw value separately so that when the input is focused we display
//   the raw number, not a formatted string. This prevents the "reversed digit"
//   bug on Capacitor WebView that occurs when a controlled input replaces the
//   value mid-typing with a reformatted string.
function StockInput({ value, onChange, className, allowNegative = false }) {
  const [focused, setFocused] = useState(false);
  const [localValue, setLocalValue] = useState("");

  const cleanNumeric = (val) => {
    const s = String(val ?? "").trim();
    const isNeg = allowNegative && s.startsWith("-");
    const digits = s.replace(/[^0-9]/g, "");
    if (!digits) return isNeg ? "-" : "";
    return isNeg ? `-${digits}` : digits;
  };

  const rawValue = cleanNumeric(value);

  const handleFocus = () => {
    setFocused(true);
    setLocalValue(rawValue);
  };

  const handleBlur = () => {
    setFocused(false);
    const finalVal = localValue === "-" ? "0" : localValue;
    onChange(finalVal);
  };

  const handleChange = (e) => {
    const raw = cleanNumeric(e.target.value);
    setLocalValue(raw);
    onChange(raw);
  };

  const displayFormatted = rawValue === "-" ? "-" : formatNumberWithSeparator(rawValue);
  const isNegativeVal = String(value ?? "").trim().startsWith("-");

  return (
    <input
      type="text"
      inputMode={allowNegative ? "text" : "numeric"}
      value={focused ? localValue : displayFormatted}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      className={`${className} ${isNegativeVal && !focused ? "text-rose-600 font-bold" : ""}`}
    />
  );
}

export default function StockConfigModal({ isOpen, onClose, onSave }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsRequestRef = useRef(0);
  const { showNotification } = useNotification();

  const toggleRow = (productId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        fetchProducts(currentPage, searchTerm, categoryFilter);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentPage, searchTerm, categoryFilter]);

  const fetchProducts = async (page = 1, query = "", categoryId = "") => {
    const requestId = ++productsRequestRef.current;
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE, query, category_id: categoryId };
      const response = await inventoryService.getStocks(params);
      if (requestId === productsRequestRef.current) {
        setProducts(response.data || []);
        setTotalProducts(response.meta?.total || 0);
      }
    } catch (error) {
      if (requestId === productsRequestRef.current) {
        console.error("Failed to fetch products:", error);
        showNotification("error", "Gagal memuat data produk");
      }
    } finally {
      if (requestId === productsRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.list({ limit: 100 });
      setCategories(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const handleStockChange = (productId, field, value) => {
    const isStockField = field === "stock";
    const s = String(value ?? "").trim();
    const isNeg = isStockField && s.startsWith("-");
    const digits = s.replace(/[^0-9]/g, "");
    const numericValue = isNeg ? (digits ? `-${digits}` : "-") : digits;

    setChanges((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        product: products.find((product) => product.id.toString() === productId),
        [field]: numericValue,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const today = new Date();
      const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const promises = Object.entries(changes).map(
        async ([productId, data]) => {
          if (data.minimum !== undefined) {
            await inventoryService.updateStockConfig(productId, {
              minimum_stock: parseInt(data.minimum, 10) || 0,
            });
          }

          if (data.stock !== undefined) {
            const product = data.product;
            const currentStock = product?.current_stock ?? 0;
            const newStock = parseInt(data.stock, 10);
            if (isNaN(newStock)) return;
            const diff = newStock - currentStock;
            if (diff !== 0) {
              const adjustment = {
                date,
                notes: "Penyesuaian stok dari konfigurasi stok",
                attachment_url: "",
                items: [
                  {
                    product_id: product?.product_id ?? productId,
                    variant_id: product?.variant_id || null,
                    option_id: product?.option_id || null,
                    type: diff > 0 ? "IN" : "OUT",
                    qty: Math.abs(diff),
                    reason: "Penyesuaian stok dari konfigurasi stok",
                  },
                ],
              };
              const adj = await inventoryService.createStockAdjustment(adjustment);
              const adjId = adj?.data?.id ?? adj?.id;
              if (adjId) {
                await inventoryService.updateAdjustmentStatus(adjId, "APPROVED");
              }
            }
          }
        },
      );

      await Promise.all(promises);
      showNotification("success", "Konfigurasi stok berhasil disimpan");
      setChanges({});
      await fetchProducts(currentPage, searchTerm, categoryFilter);
      if (typeof onSave === "function") onSave();
      onClose();
    } catch (error) {
      console.error("Failed to save stock config:", error);
      showNotification(
        "error",
        error.response?.data?.message || "Gagal menyimpan konfigurasi",
      );
    } finally {
      setSaving(false);
    }
  };

  const groupedProducts = useMemo(() => {
    const map = {};
    products.forEach((product) => {
      const key = product.product_id || product.id;
      if (!map[key]) {
        map[key] = {
          id: key,
          product_name: product.product_name || product.name,
          product_code: product.product_code || "-",
          category_name: product.category_name,
          variants: [],
        };
      }
      map[key].variants.push(product);
    });
    return Object.values(map);
  }, [products]);

  const getRawValue = (product, field) => {
    const productId = product.id.toString();
    if (changes[productId]?.[field] !== undefined) {
      return String(changes[productId][field]);
    }
    const val = field === "stock" ? product.current_stock : product.minimum_stock;
    return String(val ?? 0);
  };

  const aggregateConfigVal = (variants, field) => {
    const totals = {};
    variants.forEach((v) => {
      const raw = Number(getRawValue(v, field)) || 0;
      const unit = (v.unit_name || "PCS").toUpperCase();
      totals[unit] = (totals[unit] || 0) + raw;
    });
    return Object.entries(totals)
      .map(([unit, val]) => `${formatNumberWithSeparator(val)} ${unit}`)
      .join(", ");
  };

  if (!isOpen) return null;

  const categoryOptions = categories.map((c) => ({
    value: c.id?.toString() || c.name,
    label: c.name,
  }));
  const totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));
  const startEntry = totalProducts === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endEntry = Math.min(currentPage * PAGE_SIZE, totalProducts);

  const inputClass =
    "w-20 bg-white border border-slate-200 rounded-lg px-2 py-1 text-center text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors";
  const inputClassAlt =
    "w-20 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-center text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 w-full max-w-4xl flex flex-col max-h-[90vh] shadow-2xl overflow-hidden modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
              Konfigurasi Stok
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Penyesuaian jumlah dan minimum stok sebelum sistem digunakan
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search + Kategori filter */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/40 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Cari data produk disini..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
            />
          </div>
          <div className="relative md:w-60">
            <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors appearance-none">
              <option value="">Semua Kategori</option>
              {categoryOptions.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 animate-spin text-teal-600" />
              <span className="ml-2 text-slate-600 text-sm">Memuat data...</span>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50/80 sticky top-0">
                <tr className="border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Nama Produk
                  </th>
                  <th className="px-6 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">
                    Stok
                  </th>
                  <th className="px-6 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-32">
                    Minimum
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groupedProducts.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-8 text-center text-slate-500 text-sm">
                      {searchTerm || categoryFilter
                        ? "Tidak ada produk yang cocok dengan filter"
                        : "Tidak ada data produk"}
                    </td>
                  </tr>
                ) : (
                  groupedProducts.map((product) => {
                    const hasMultiple = product.variants.length > 1;
                    const isExpanded = !!expandedRows[product.id];

                    if (hasMultiple) {
                      return (
                        <React.Fragment key={product.id}>
                          {/* Parent Row */}
                          <tr className="hover:bg-slate-50/50 bg-white transition-colors font-medium">
                            <td className="px-6 py-4 text-sm text-slate-900">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleRow(product.id)}
                                  className="p-1 hover:bg-slate-100 rounded text-slate-500 transition-colors"
                                  title={isExpanded ? "Collapse" : "Expand"}>
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                                <span className="font-bold text-slate-800">{product.product_name}</span>
                                {product.product_code && product.product_code !== "-" && (
                                  <span className="inline-flex items-center text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200 font-semibold px-1.5 py-0.5 rounded">
                                    Kode: {product.product_code}
                                  </span>
                                )}
                                <span className="text-xs text-teal-600 font-semibold">
                                  ({product.variants.length} Varian)
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center text-slate-500 text-xs font-normal">
                              {aggregateConfigVal(product.variants, "stock")}
                            </td>
                            <td className="px-6 py-4 text-center text-slate-500 text-xs font-normal">
                              {aggregateConfigVal(product.variants, "minimum")}
                            </td>
                          </tr>

                          {/* Expanded Variant Inputs */}
                          {isExpanded &&
                            product.variants.map((v) => (
                              <tr
                                key={v.id}
                                className="transition-colors bg-slate-50/40 hover:bg-slate-100/50 border-l-4 border-teal-500/30">
                                <td className="px-6 py-3 text-sm text-slate-900">
                                  <div className="pl-8 text-xs text-slate-600 font-medium">
                                    ↳ {v.option_id ? `Opsi ${v.option_name || "Extra"}` : `Varian ${v.variant_name || "DEFAULT"}`} | Satuan {v.unit_name || "-"}
                                  </div>
                                </td>
                                <td className="px-6 py-3 text-center">
                                  <StockInput
                                    value={getRawValue(v, "stock")}
                                    onChange={(val) => handleStockChange(v.id.toString(), "stock", val)}
                                    allowNegative={true}
                                    className={inputClass}
                                  />
                                </td>
                                <td className="px-6 py-3 text-center">
                                  <StockInput
                                    value={getRawValue(v, "minimum")}
                                    onChange={(val) => handleStockChange(v.id.toString(), "minimum", val)}
                                    allowNegative={false}
                                    className={inputClass}
                                  />
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    } else {
                      const v = product.variants[0];
                      return (
                        <tr key={v.id} className="hover:bg-slate-50/70 transition-colors">
                          <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                            <div className="font-semibold text-slate-800">{product.product_name}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-slate-500">
                              {product.product_code && product.product_code !== "-" && (
                                <span className="inline-flex items-center text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200 font-semibold px-1.5 py-0.5 rounded">
                                  Kode: {product.product_code}
                                </span>
                              )}
                              <span className="font-normal">
                                {v.variant_name ? `Merek ${v.variant_name} • ` : ""}Satuan {v.unit_name || "-"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <StockInput
                              value={getRawValue(v, "stock")}
                              onChange={(val) => handleStockChange(v.id.toString(), "stock", val)}
                              allowNegative={true}
                              className={inputClassAlt}
                            />
                          </td>
                          <td className="px-6 py-4 text-center">
                            <StockInput
                              value={getRawValue(v, "minimum")}
                              onChange={(val) => handleStockChange(v.id.toString(), "minimum", val)}
                              allowNegative={false}
                              className={inputClassAlt}
                            />
                          </td>
                        </tr>
                      );
                    }
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-xs text-slate-500">
            Menampilkan {startEntry}-{endEntry} dari {formatNumberWithSeparator(totalProducts)} data stok
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={loading || currentPage === 1}
              className="h-8 px-3 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft className="w-4 h-4" />
              Sebelumnya
            </button>
            <span className="min-w-24 text-center text-xs font-medium text-slate-600">
              Halaman {currentPage} dari {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={loading || currentPage >= totalPages}
              className="h-8 px-3 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Berikutnya
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors disabled:opacity-50">
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={saving || Object.keys(changes).length === 0}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2">
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}
